import formatDateTime from 'utils/formatDateTime';
import { geoFromIP } from 'utils/geo';

let SHOWTIMES_API_ROOT = 'https://showtimes-v2.s-prod.pow.io/v2.0/';
// let SHOWTIMES_API_ROOT = 'http://localhost:3300/v2.0/';
const todayString = () => formatDateTime(new Date(), 'yyyy-MM-dd');
const defaultLocalTime = () => ({ local_time: formatDateTime(new Date(), 'yyyy-MM-dd HH:mm:ss') });

export async function getNextShowtimes(movieIds) {
	const location = await geoFromIP();
	const url = new URL('universal/next-screenings-nearby', SHOWTIMES_API_ROOT);
	url.searchParams.set('movie_id', movieIds.join(','));
	url.searchParams.set('showtimes_providers', 'base:webedia,fandango,amc,pow');
	url.searchParams.set('local_time', defaultLocalTime().local_time);
	url.searchParams.set('today', todayString());
	const res = await fetch(url, {
		headers: {
			'x-requested-lat': location.lat,
			'x-requested-lon': location.lon,
		}
	});
	if (!res.ok) throw new Error(`Error fetching next showtimes: ${res.statusText}`);
	const data = await res.json();
	return data.response.nearbyScreeningsByMovie;
}

function cacheEngine(options = {}) {
	const {
		key = e => e.id,
		filter,
		request,
		extract,
		defaultValue = null,
	} = options;
	const cache = new Map();
	return async (items) => {
		const missing = items.filter(item => filter?.(item) && !cache.has(key(item)));
		if (missing.length) {
			const promise = request(missing);
			missing.forEach(item => cache.set(key(item), promise.then(o => extract(o, item))));
		}
		return Promise.all(items.map(item => cache.get(key(item)) || defaultValue));
	};
}

const moviePagesDetails = cacheEngine({
	filter: movie => movie?.id && movie.thundr_id,
	request: async (missing) => {
		try {
			const url = new URL('universal/movie-pages', SHOWTIMES_API_ROOT);
			// url.searchParams.set('m', missing.map(m => m.thundr_id).join(','));
			const request = await fetch(url.href, {
				// method: 'GET',
				method: 'POST',
				body: JSON.stringify(missing.map(m => ({ id: m.thundr_id }))),
				headers: { 'Content-Type': 'application/json' },
			});
			if (!request.ok) {
				throw new Error(`Failed to fetch movie page data: ${request.statusText}`);
			}
			return await request.json();
		} catch {
			return {};
		}
	},
	extract: (data, movie) => data?.[movie.thundr_id] || null,
});

async function singleMoviePageDetails(movie) {
	const data = await moviePagesDetails([movie]);
	return data[0];
}

const checkMoviesEarlyScreenings = cacheEngine({
	filter: movie => movie?.id && movie?.showtimesId && movie?.dates?.release,
	request: async (missing) => {
		try {
			const url = new URL('universal/has-screenings', SHOWTIMES_API_ROOT);
			// Using POST (not semantic) to avoid URL length limits for many movies
			const request = await fetch(url.href, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(
					missing.map(m => ({
						id: m.showtimesId,
						before: m.dates.release,
						// TODO: dynamic region ?
						region: 'us',
					}))
				),
			});
			if (!request.ok) {
				throw new Error(`Failed to fetch screenings data: ${request.statusText}`);
			}
			return await request.json();
		} catch {
			return {};
		}
	},
	extract: (data, movie) => data?.[movie.showtimesId] || false,
	defaultValue: false,
});

async function checkSingleMovieEarlyScreenings(movie) {
	const data = await checkMoviesEarlyScreenings([movie]);
	return data[0];
}

// statuses without considering screenings (anything excluding "preSale")
async function getBaseStatus(movie) {
	const status = { theaters: 'unknown', homeEnt: 'unknown' };

	const pages = await singleMoviePageDetails(movie);

	if (pages?.homeEnt) {
		status.homeEnt = 'homeEnt';
		status.homeEntPath = pages.homeEnt.path;
	}

	const today = formatDateTime(new Date(), 'yyyy-MM-dd');
	const releaseDate = movie?.dates?.release;
	if (pages?.showtimes) {
		status.showtimesPath = pages.showtimes.path;
	}
	if (releaseDate) {
		if (today < releaseDate) {
			status.theaters = 'comingSoon';
		} else if (pages?.showtimes) {
			status.theaters = 'inTheaters';
		}
	}

	return status;
}

export async function getMovieStatus(movie) {
	const status = await getBaseStatus(movie);

	const pages = await singleMoviePageDetails(movie);
	const today = formatDateTime(new Date(), 'yyyy-MM-dd');
	const ticketingDate = movie?.dates?.ticketing;
	if (status.theaters === 'comingSoon' && pages?.showtimes && today >= ticketingDate) {
		const hasEarlyScreenings = await checkSingleMovieEarlyScreenings(movie);
		if (hasEarlyScreenings) {
			status.theaters = 'preSale';
		}
	}

	return status;
}

export async function getAllMovieStatuses(movies) {
	// Run a single query getting all the necessary movie pages data and cache things
	moviePagesDetails(movies);
	const baseStatuses = await Promise.all(movies.map(async m => ({ movie: m, status: await getBaseStatus(m) })));
	const screeningsDataNeeded = baseStatuses.filter(v => v.status?.theaters === 'comingSoon').map(m => m.movie);
	// Get all the necessary server data in a single API request and cache things
	checkMoviesEarlyScreenings(screeningsDataNeeded);
	const entries = await Promise.all(movies.map(async m => m?.id && [m.id, await getMovieStatus(m)]));
	return Object.fromEntries(entries.filter(Boolean));
}